// Player-name validation + reservation-key derivation. Used client-side for
// pre-validation; mirrored in functions/src/playerName.ts for the authoritative
// server check (functions/ can't import @spirits/shared at runtime).
// KEEP IN LOCKSTEP with the mirror.

import {
  MAX_PLAYER_NAME_CHARS,
  MIN_PLAYER_NAME_CHARS,
  PLAYER_NAME_AUTO_ADJECTIVES,
  PLAYER_NAME_AUTO_NOUNS,
  PLAYER_NAME_BLOCKLIST,
} from './constants.js';

export interface ValidateOk {
  ok: true;
  normalized: string;          // the canonical display form to store on the user doc
  reservationKey: string;      // the lowercased + diacritic-folded key for /playerNames/{key}
}
export interface ValidateErr {
  ok: false;
  error: string;               // user-safe error text
}
export type ValidateResult = ValidateOk | ValidateErr;

// Strip control characters, zero-width, bidi marks. These would let a name
// render differently than its reservation key.
function stripDangerousCodePoints(s: string): string {
  let out = '';
  for (const ch of s) {
    const c = ch.codePointAt(0) || 0;
    if (c <= 0x1f || c === 0x7f) continue;
    if (c >= 0x200b && c <= 0x200f) continue;
    if (c >= 0x202a && c <= 0x202e) continue;
    if (c === 0x2060 || c === 0xfeff) continue;
    out += ch;
  }
  return out;
}

// Reservation key: NFKD, lowercase, drop combining marks (so "José" and "Jose"
// collide), drop everything that isn't a letter or digit. Enforces case- and
// diacritic-insensitive uniqueness for /playerNames/{key}.
export function reservationKeyFor(displayName: string): string {
  return displayName
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

// Charset: letters, marks (for languages where the base+combining is the
// rendered form), digits, space, hyphen, apostrophe. Must contain at least
// one letter so an all-digits name isn't allowed.
const ALLOWED_RE = /^[\p{L}\p{M}\p{N} '\-]+$/u;
const HAS_LETTER_RE = /\p{L}/u;

export function validatePlayerName(raw: string): ValidateResult {
  if (typeof raw !== 'string') return { ok: false, error: 'name must be text' };

  const cleaned = stripDangerousCodePoints(raw).normalize('NFKC').trim().replace(/\s+/g, ' ');

  if (cleaned.length < MIN_PLAYER_NAME_CHARS) {
    return { ok: false, error: `at least ${MIN_PLAYER_NAME_CHARS} characters` };
  }
  if (cleaned.length > MAX_PLAYER_NAME_CHARS) {
    return { ok: false, error: `at most ${MAX_PLAYER_NAME_CHARS} characters` };
  }
  if (!ALLOWED_RE.test(cleaned)) {
    return { ok: false, error: 'letters, numbers, spaces, apostrophes, hyphens only' };
  }
  if (!HAS_LETTER_RE.test(cleaned)) {
    return { ok: false, error: 'must include at least one letter' };
  }

  const key = reservationKeyFor(cleaned);
  if (key.length < MIN_PLAYER_NAME_CHARS) {
    return { ok: false, error: 'too short after normalization' };
  }
  if (PLAYER_NAME_BLOCKLIST.includes(key)) {
    return { ok: false, error: 'that name is reserved' };
  }

  return { ok: true, normalized: cleaned, reservationKey: key };
}

// Deterministic auto-name generator: same uid → same starting pair across
// restarts (so anon accounts feel stable). Caller is responsible for
// retrying with a suffix on collision.
function hashCode(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function autoPlayerName(seed: string, attempt = 0): string {
  const h = hashCode(seed + (attempt > 0 ? `#${attempt}` : ''));
  const adj = PLAYER_NAME_AUTO_ADJECTIVES[h % PLAYER_NAME_AUTO_ADJECTIVES.length];
  const noun =
    PLAYER_NAME_AUTO_NOUNS[Math.floor(h / PLAYER_NAME_AUTO_ADJECTIVES.length) % PLAYER_NAME_AUTO_NOUNS.length];
  const base = `${adj} ${noun}`;
  return attempt > 0 ? `${base} ${attempt}` : base;
}
